if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.variantGalleries = this.parseVariantGalleries();

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      const galleryData = this.querySelector('[data-variant-galleries]');
      this.filterByVariant(galleryData?.dataset.selectedVariantId);
      this.mql.addEventListener('change', () => this.refreshVariantCounter());
      if (this.elements.thumbnails && this.dataset.desktopLayout !== 'stacked' && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
      this.updateCounter(event.detail.currentElement);
    }

    parseVariantGalleries() {
      const data = this.querySelector('[data-variant-galleries]');
      if (!data) return {};
      try {
        return JSON.parse(data.textContent);
      } catch (error) {
        return {};
      }
    }

    mediaId(element) {
      return String(element?.dataset.mediaId || element?.dataset.target || '').split('-').pop();
    }

    filterByVariant(variantId, preferredMediaId) {
      const allowed = (this.variantGalleries[String(variantId)] || []).map(String);
      const filtering = allowed.length > 0;
      const mediaItems = [...this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]')];

      mediaItems.forEach((item) => {
        const hidden = filtering && !allowed.includes(this.mediaId(item));
        item.hidden = hidden;
        item.setAttribute('aria-hidden', String(hidden));
      });

      this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((thumbnail) => {
        const hidden = filtering && !allowed.includes(this.mediaId(thumbnail));
        thumbnail.hidden = hidden;
        thumbnail.setAttribute('aria-hidden', String(hidden));
      });

      const modal = document.getElementById(this.id.replace('MediaGallery-', 'ProductModal-'));
      modal?.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach((item) => {
        item.hidden = filtering && !allowed.includes(this.mediaId(item));
      });

      const visibleItems = mediaItems.filter((item) => !item.hidden);
      if (!visibleItems.length) return;
      const preferredId = String(preferredMediaId || '').split('-').pop();
      const activeItem = visibleItems.find((item) => this.mediaId(item) === preferredId) || visibleItems[0];
      mediaItems.forEach((item) => item.classList.toggle('is-active', item === activeItem));
      this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${activeItem.dataset.mediaId}"]`));
      this.updateCounter(activeItem);
      [0, 50, 250].forEach((delay) => window.setTimeout(() => {
        this.elements.viewer.resetPages?.();
        this.updateCounter(activeItem);
      }, delay));
    }

    refreshVariantCounter() {
      const activeItem = this.elements.viewer.querySelector('.product__media-list > [data-media-id].is-active:not([hidden])')
        || this.elements.viewer.querySelector('.product__media-list > [data-media-id]:not([hidden])');
      if (!activeItem) return;
      [0, 50, 250].forEach((delay) => window.setTimeout(() => this.updateCounter(activeItem), delay));
    }

    updateCounter(activeItem) {
      const visibleItems = [...this.elements.viewer.querySelectorAll('.product__media-list > [data-media-id]:not([hidden])')];
      const current = Math.max(visibleItems.indexOf(activeItem) + 1, 1);
      const currentElement = this.elements.viewer.querySelector('.slider-counter--current');
      const totalElement = this.elements.viewer.querySelector('.slider-counter--total');
      if (currentElement) currentElement.textContent = current;
      if (totalElement) totalElement.textContent = visibleItems.length;
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia || activeMedia.hidden) return;
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.updateCounter(activeMedia);
      this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition || 1);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
